import { Spin } from 'antd';
import classNames from 'classnames';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Vditor from 'vditor';
import { messageError } from '../../../components/message';
import getFormatMessage, {
  getLanguage,
} from '../../../locales/get-format-message';
import '../style.vditor.scss';

const MAX_LENGTH = 2000;

interface IProps {
  readonly loading?: boolean;
  readonly initValue?: string;
  readonly onChange?: (val: string) => void;
  readonly id: string;
  readonly errorMessage?: string;
  readonly placeholder?: string;
  readonly contentExample?: string;
  readonly disabled?: boolean;
  readonly customAction?: Array<IMenuItem>;
  readonly onInit?: () => void;
  readonly uploadHandler?: (file: File) => void;
}

export interface IVDRef {
  validate: () => boolean;
  getValue: () => string;
}

export interface IVDitorRef {
  validate: (inputValue?: string | undefined) => boolean;
  getValue: () => string;
  insertValue: (val: string) => void;
  setValue: (val: string) => void;
}

export const VDitorInput = forwardRef((props: IProps, ref) => {
  const {
    loading,
    disabled,
    customAction,
    onInit,
    initValue,
    onChange,
    uploadHandler,
  } = props;
  const [errMsg, setErrMsg] = useState<string>(props.errorMessage || '');
  const vdInstance = useRef<Vditor>();
  const [vdState, setVdState] = useState<string>('');

  useImperativeHandle(ref, () => ({
    validate,
    getValue,
    insertValue: (val: string) => {
      vdInstance.current?.insertValue(val);
    },
    setValue: (val: string) => {
      const mdContent = vdInstance.current?.html2md(val) || '';
      vdInstance.current?.setValue(mdContent);
      onChange?.(val);
    },
  }));

  useEffect(() => {
    if (!vdInstance.current || !initValue) {
      return;
    }
    const mdContent = vdInstance.current.html2md(initValue) || '';
    vdInstance.current.setValue(mdContent);
  }, [initValue, vdState]);

  useEffect(() => {
    if (disabled) {
      const container = document.querySelector(`#${props.id}`) as
        | HTMLElement
        | undefined;
      if (!container || !initValue) {
        return;
      }
      container.innerHTML = initValue;
      return;
    }

    const toolbar: (string | IMenuItem)[] = [
      'undo',
      'redo',
      '|',
      'headings',
      'bold',
      'italic',
      'strike',
      'link',
      '|',
      'list',
      'ordered-list',
      'check',
      'outdent',
      'indent',
      '|',
      'quote',
      'table',
      '|',
      'edit-mode',
      'upload',
    ];
    if (customAction) {
      toolbar.push(...customAction);
    }
    const vditor = new Vditor(props.id, {
      lang: getLanguage || 'zh_CN',
      placeholder: props.placeholder,
      toolbar,
      mode: 'wysiwyg',
      input: onVdInput,
      after: () => {
        onInit?.();
        setVdState('init');
        vdInstance.current = vditor;
        if (props.initValue) {
          const mdContent = vditor.html2md(props.initValue) || '';
          vditor.setValue(mdContent);
          return;
        }

        vditor.setValue(props.contentExample || '');
      },
      upload: {
        accept: 'image/*',
        handler: async (files): Promise<null> => {
          if (files[0].size > 2 * 1024 * 1024) {
            messageError(getFormatMessage('请上传2M以内的图片'));
            return null;
          }
          if (uploadHandler) {
            const link = await uploadHandler?.(files[0]);
            if (vditor.vditor.currentMode === 'wysiwyg') {
              vdInstance.current?.insertValue(`<img src="${link}">`); // 富文本模式
            } else {
              vdInstance.current?.insertValue(`![img](${link})`); // Markdown模式
            }
          }
          return null;
        },
      },
    });
  }, []);

  const getValue = () => {
    return vdInstance.current?.getHTML() || '';
  };

  const validate = (inputValue?: string) => {
    const value = inputValue || vdInstance.current?.getHTML() || '';

    if (!value?.trim()) {
      setErrMsg(props.placeholder || getFormatMessage('please_update_content'));
      return false;
    }

    if (value?.trim().length > MAX_LENGTH) {
      setErrMsg(
        getFormatMessage('exceed_character_limit', {
          params: {
            maxLength: MAX_LENGTH,
          },
        })
      );
      return false;
    }
    setErrMsg('');
    return true;
  };

  const onVdInput = () => {
    onChange?.(getValue());
  };

  return (
    <Spin spinning={loading}>
      <div
        id={props.id}
        className={classNames('vditor', errMsg ? 'error' : '')}
      />
    </Spin>
  );
});
